import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, TextField, MenuItem, Grid, Switch, FormControlLabel,
  Autocomplete, CircularProgress, Stepper, Step, StepLabel, InputAdornment, Alert, Snackbar, Divider,
} from '@mui/material';
import { Save, VerifiedUser, ArrowForward, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import {
  P, cardSx, fieldSx, priBtn, outBtn, FieldLabel, WorkspaceHeader, StatusPill, ValidationStrip,
} from '../../components/common/workspaceKit';
import { productService, ProductInput, ProductPolicy, ProductTypeMeta } from '../../services/productService';
import { uomService, Uom } from '../../services/uomService';
import { categoryService, Category } from '../../services/categoryService';
import { hsnService, HsnCode } from '../../services/hsnService';
import { coaService, CoaAccount } from '../../services/coaService';
import AccountSelect from '../../components/finance/AccountSelect';

// ---------------------------------------------------------------------------
// Product Master — enterprise create/edit as an 8-step workspace whose steps
// and fields adapt to the chosen Product Type (progressive disclosure). Writes
// through productService.createProduct/updateProduct (camelCase ProductInput).
// ---------------------------------------------------------------------------

type Form = Record<string, any>;

const emptyForm = (): Form => ({
  productType: 'RAW_MATERIAL',
  productName: '', category: '', brand: '', manufacturer: '', model: '', sku: '',
  shortDescription: '', description: '',
  uomId: '', purchaseUomId: '', salesUomId: '', stockUomId: '',
  isStockItem: true, isBatchTracked: false, isSerialTracked: false,
  shelfLifeDays: '', reorderLevel: '', minStock: '', maxStock: '', safetyStock: '', leadTimeDays: '', eoq: '',
  canPurchase: true, purchasePrice: 0, preferredVendorId: '',
  canSell: false, basePrice: 0,
  hsnCode: '', sacCode: '', gstRate: 18, isTaxInclusive: false, reverseCharge: false,
  inventoryAccountId: '', purchaseAccountId: '', salesAccountId: '', cogsAccountId: '', expenseAccountId: '', consumptionAccountId: '',
  qcRequired: false, incomingInspection: false, processQc: false, finalQc: false, makeOrBuy: 'BUY', assetCategoryId: '',
  canStock: true, canManufacture: false, canConsume: true, canCapitalize: false,
  isActive: true, itemStatus: 'ACTIVE',
});

const GST_RATES = [0, 5, 12, 18, 28];

const ProductWorkspacePage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const [policy, setPolicy] = useState<ProductPolicy | null>(null);
  const [uoms, setUoms] = useState<Uom[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [accounts, setAccounts] = useState<CoaAccount[]>([]);
  const [hsnOptions, setHsnOptions] = useState<HsnCode[]>([]);
  const [hsnInput, setHsnInput] = useState('');

  const [f, setF] = useState<Form>(emptyForm());
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const set = (k: string, v: any) => setF((prev) => ({ ...prev, [k]: v }));

  const typeMeta: ProductTypeMeta | undefined = useMemo(
    () => policy?.productTypes.find((t) => t.key === f.productType),
    [policy, f.productType]
  );
  const stockable = !!typeMeta?.stockable;
  const isService = f.productType === 'SERVICE';
  const isCapital = f.productType === 'CAPITAL_GOODS';

  // Load reference data + (on edit) the product.
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [pol, um, cat, acc] = await Promise.all([
          productService.getPolicy(),
          uomService.getUoms({ activeOnly: true }),
          categoryService.getCategories(),
          coaService.accounts().catch(() => ({ accounts: [] as CoaAccount[] })),
        ]);
        setPolicy(pol);
        setUoms(um.uoms || []);
        setCategories(cat.categories || []);
        setAccounts(acc.accounts || []);
        setHsnOptions((await hsnService.getAll({ limit: 50 })).hsnCodes || []);
        if (isEdit && id) {
          const { product } = await productService.getProduct(id);
          setF({
            ...emptyForm(),
            productType: product.product_type || 'RAW_MATERIAL',
            productName: product.product_name || '',
            category: product.category || '',
            brand: product.brand || '', manufacturer: product.manufacturer || '', model: product.model || '', sku: product.sku || '',
            shortDescription: product.short_description || '', description: product.description || '',
            uomId: product.uom_id || '', purchaseUomId: product.purchase_uom_id || '', salesUomId: product.sales_uom_id || '', stockUomId: product.stock_uom_id || '',
            isStockItem: !!product.is_stock_item, isBatchTracked: !!product.is_batch_tracked, isSerialTracked: !!product.is_serial_tracked,
            shelfLifeDays: product.shelf_life_days ?? '', reorderLevel: product.reorder_level ?? '', minStock: product.min_stock ?? '', maxStock: product.max_stock ?? '',
            safetyStock: product.safety_stock ?? '', leadTimeDays: product.lead_time_days ?? '', eoq: product.eoq ?? '',
            canPurchase: !!product.can_purchase, purchasePrice: product.purchase_price ?? 0, preferredVendorId: product.preferred_vendor_id ?? '',
            canSell: !!product.can_sell, basePrice: product.base_price ?? 0,
            hsnCode: product.hsn_code || '', sacCode: product.sac_code || '', gstRate: product.gst_rate != null ? Number(product.gst_rate) : 18,
            isTaxInclusive: !!product.is_tax_inclusive, reverseCharge: !!product.reverse_charge,
            inventoryAccountId: product.inventory_account_id ?? '', purchaseAccountId: product.purchase_account_id ?? '', salesAccountId: product.sales_account_id ?? '',
            cogsAccountId: product.cogs_account_id ?? '', expenseAccountId: product.expense_account_id ?? '', consumptionAccountId: product.consumption_account_id ?? '',
            qcRequired: !!product.qc_required, incomingInspection: !!product.incoming_inspection, processQc: !!product.process_qc, finalQc: !!product.final_qc, makeOrBuy: product.make_or_buy || 'BUY', assetCategoryId: product.asset_category_id ?? '',
            canStock: !!product.can_stock, canManufacture: !!product.can_manufacture, canConsume: !!product.can_consume, canCapitalize: !!product.can_capitalize,
            isActive: product.is_active, itemStatus: product.item_status || 'ACTIVE',
            hsnCode_: product.hsn_code,
          });
          setHsnInput(product.hsn_code || '');
          // Load the code's real description so the HSN field shows "3302 — <desc>"
          // instead of a bare "3302 — " when it isn't in the first option page.
          if (product.hsn_code) {
            hsnService.search(product.hsn_code)
              .then((r) => {
                const found = (r.hsnCodes || []).find((h) => h.hsn_code === product.hsn_code);
                if (found) setHsnOptions((prev) => (prev.some((h) => h.hsn_code === found.hsn_code) ? prev : [found, ...prev]));
              })
              .catch(() => { /* keep the code-only label */ });
          }
        }
      } catch (e: any) {
        setError(e.message || 'Failed to load reference data');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Picking a product type resets capability flags + stockability to its defaults.
  const onTypeChange = (key: string) => {
    const meta = policy?.productTypes.find((t) => t.key === key);
    const caps = meta?.capabilities || {};
    setF((prev) => ({
      ...prev,
      productType: key,
      canPurchase: !!caps.can_purchase, canSell: !!caps.can_sell, canStock: !!caps.can_stock,
      canManufacture: !!caps.can_manufacture, canConsume: !!caps.can_consume, canCapitalize: !!caps.can_capitalize,
      isStockItem: !!(meta?.stockable),
      makeOrBuy: caps.can_manufacture ? 'MAKE' : 'BUY',
    }));
  };

  const searchHsn = useCallback(async (q: string) => {
    if (q.length < 2) return;
    try { setHsnOptions((await hsnService.search(q)).hsnCodes || []); } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    // Don't re-search when the input already matches the selected code — this
    // (together with the memoised value below) stops the search-on-loop when
    // editing a product whose HSN is already chosen.
    if (!hsnInput || hsnInput.split(' ')[0] === f.hsnCode) return;
    const t = setTimeout(() => searchHsn(hsnInput), 300);
    return () => clearTimeout(t);
  }, [hsnInput, f.hsnCode, searchHsn]);

  // Referentially-stable selected HSN option. A new object literal here every
  // render made the freeSolo Autocomplete re-sync inputValue → re-render loop.
  const hsnValue = useMemo(
    () => hsnOptions.find((h) => h.hsn_code === f.hsnCode) || (f.hsnCode ? { hsn_code: f.hsnCode, description: '', gst_rate: f.gstRate } : null),
    [hsnOptions, f.hsnCode, f.gstRate],
  );

  // Step definitions, filtered by product type.
  const ALL_STEPS = [
    { key: 'identity', label: 'Identity', visible: () => true },
    { key: 'units', label: 'Units & Inventory', visible: () => true },
    { key: 'procurement', label: 'Procurement', visible: () => true },
    { key: 'sales', label: 'Sales', visible: () => true },
    { key: 'tax', label: 'Tax', visible: () => true },
    { key: 'accounting', label: 'Accounting', visible: () => true },
    { key: 'quality', label: 'Quality / Production', visible: () => !isService && f.productType !== 'NON_STOCK' },
    { key: 'review', label: 'Review', visible: () => true },
  ];
  const steps = ALL_STEPS.filter((s) => s.visible());
  const step = steps[Math.min(activeStep, steps.length - 1)];

  const nameOk = String(f.productName || '').trim().length >= 2;
  const typeOk = !!f.productType;

  const save = async () => {
    if (!nameOk || !typeOk) { setActiveStep(0); setError('Product name (2+ chars) and type are required'); return; }
    setSaving(true); setError(null);
    const numOrU = (v: any) => (v === '' || v === null || v === undefined ? undefined : Number(v));
    const payload: ProductInput = {
      productName: String(f.productName).trim(),
      productType: f.productType,
      category: f.category || undefined,
      brand: f.brand || undefined, manufacturer: f.manufacturer || undefined, model: f.model || undefined, sku: f.sku || undefined,
      shortDescription: f.shortDescription || undefined, description: f.description || undefined,
      uomId: numOrU(f.uomId), purchaseUomId: numOrU(f.purchaseUomId), salesUomId: numOrU(f.salesUomId), stockUomId: numOrU(f.stockUomId),
      unitOfMeasure: uoms.find((u) => u.id === Number(f.uomId))?.uom_code || undefined,
      isStockItem: !!f.isStockItem, isBatchTracked: !!f.isBatchTracked, isSerialTracked: !!f.isSerialTracked,
      shelfLifeDays: numOrU(f.shelfLifeDays), reorderLevel: numOrU(f.reorderLevel), minStock: numOrU(f.minStock), maxStock: numOrU(f.maxStock),
      safetyStock: numOrU(f.safetyStock), leadTimeDays: numOrU(f.leadTimeDays), eoq: numOrU(f.eoq),
      canPurchase: !!f.canPurchase, canSell: !!f.canSell, canStock: !!f.canStock,
      canManufacture: !!f.canManufacture, canConsume: !!f.canConsume, canCapitalize: !!f.canCapitalize,
      purchasePrice: Number(f.purchasePrice) || 0, basePrice: Number(f.basePrice) || 0,
      preferredVendorId: numOrU(f.preferredVendorId),
      hsnCode: f.hsnCode || undefined, sacCode: f.sacCode || undefined, gstRate: Number(f.gstRate) || 0,
      isTaxInclusive: !!f.isTaxInclusive, reverseCharge: !!f.reverseCharge,
      inventoryAccountId: numOrU(f.inventoryAccountId), purchaseAccountId: numOrU(f.purchaseAccountId), salesAccountId: numOrU(f.salesAccountId),
      cogsAccountId: numOrU(f.cogsAccountId), expenseAccountId: numOrU(f.expenseAccountId), consumptionAccountId: numOrU(f.consumptionAccountId),
      qcRequired: !!f.qcRequired, incomingInspection: !!f.incomingInspection, processQc: !!f.processQc, finalQc: !!f.finalQc, makeOrBuy: f.makeOrBuy, assetCategoryId: numOrU(f.assetCategoryId),
      isActive: !!f.isActive, itemStatus: f.itemStatus,
    };
    try {
      if (isEdit && id) await productService.updateProduct(id, payload);
      else await productService.createProduct(payload);
      setToast('Product saved'); setTimeout(() => navigate('/sales/products'), 500);
    } catch (e: any) {
      setError(e.message || 'Failed to save product');
    } finally { setSaving(false); }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  const uomSelect = (label: string, k: string) => (
    <TextField select fullWidth size="small" value={f[k] || ''} onChange={(e) => set(k, e.target.value)} sx={fieldSx}>
      <MenuItem value=""><em>None</em></MenuItem>
      {uoms.map((u) => <MenuItem key={u.id} value={u.id}>{u.uom_code} — {u.uom_name}</MenuItem>)}
    </TextField>
  );
  const numField = (k: string, ph = '0') => (
    <TextField fullWidth size="small" type="number" value={f[k]} onChange={(e) => set(k, e.target.value)} placeholder={ph} sx={fieldSx} />
  );
  const toggle = (k: string, label: string) => (
    <FormControlLabel control={<Switch checked={!!f[k]} onChange={(e) => set(k, e.target.checked)} />} label={label} />
  );

  return (
    <Box sx={{ bgcolor: P.bg, minHeight: '100vh', pb: 6 }}>
      <WorkspaceHeader
        onBack={() => navigate('/sales/products')}
        title={isEdit ? 'Edit Product' : 'New Product'}
        subtitle={typeMeta ? `${typeMeta.label} · code auto-assigned with prefix ${typeMeta.prefix}` : 'Pick a product type to begin'}
        chip={<StatusPill label={f.isActive ? (f.itemStatus || 'ACTIVE') : 'INACTIVE'} tone={f.isActive ? 'green' : 'muted'} />}
        actions={
          <>
            <Button variant="outlined" onClick={() => navigate('/sales/products')} sx={outBtn}>Cancel</Button>
            <Button variant="contained" startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <VerifiedUser sx={{ fontSize: 18 }} />} onClick={save} disabled={saving} sx={priBtn}>
              {saving ? 'Saving…' : isEdit ? 'Update Product' : 'Create Product'}
            </Button>
          </>
        }
      />

      <ValidationStrip items={[{ ok: nameOk, label: 'Product name' }, { ok: typeOk, label: 'Product type' }]} />

      <Box sx={{ px: { xs: 2, md: 3 }, pt: 1 }}>
        {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '10px' }}>{error}</Alert>}

        <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 3 } }}>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
            {steps.map((s) => <Step key={s.key}><StepLabel>{s.label}</StepLabel></Step>)}
          </Stepper>

          {/* STEP: Identity */}
          {step.key === 'identity' && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <FieldLabel text="Product Type" required />
                <TextField select fullWidth size="small" value={f.productType} onChange={(e) => onTypeChange(e.target.value)} sx={fieldSx}>
                  {policy?.productTypes.map((t) => <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 8 }}>
                <FieldLabel text="Product Name" required />
                <TextField fullWidth size="small" value={f.productName} onChange={(e) => set('productName', e.target.value)} sx={fieldSx} />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FieldLabel text="Category" />
                <Autocomplete freeSolo size="small" options={categories.map((c) => c.name)} value={f.category}
                  onChange={(_, v) => set('category', v || '')} onInputChange={(_, v) => set('category', v)}
                  renderInput={(p) => <TextField {...p} sx={fieldSx} placeholder="Select or type" />} />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}><FieldLabel text="Brand" /><TextField fullWidth size="small" value={f.brand} onChange={(e) => set('brand', e.target.value)} sx={fieldSx} /></Grid>
              <Grid size={{ xs: 12, md: 4 }}><FieldLabel text="Manufacturer" /><TextField fullWidth size="small" value={f.manufacturer} onChange={(e) => set('manufacturer', e.target.value)} sx={fieldSx} /></Grid>
              <Grid size={{ xs: 12, md: 4 }}><FieldLabel text="Model" /><TextField fullWidth size="small" value={f.model} onChange={(e) => set('model', e.target.value)} sx={fieldSx} /></Grid>
              <Grid size={{ xs: 12, md: 4 }}><FieldLabel text="SKU" /><TextField fullWidth size="small" value={f.sku} onChange={(e) => set('sku', e.target.value)} sx={fieldSx} /></Grid>
              <Grid size={{ xs: 12 }}><FieldLabel text="Short Description" /><TextField fullWidth size="small" value={f.shortDescription} onChange={(e) => set('shortDescription', e.target.value)} sx={fieldSx} /></Grid>
              <Grid size={{ xs: 12 }}><FieldLabel text="Description" /><TextField fullWidth size="small" multiline minRows={2} value={f.description} onChange={(e) => set('description', e.target.value)} sx={fieldSx} /></Grid>
            </Grid>
          )}

          {/* STEP: Units & Inventory */}
          {step.key === 'units' && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 3 }}><FieldLabel text="Primary UOM" />{uomSelect('Primary UOM', 'uomId')}</Grid>
              <Grid size={{ xs: 12, md: 3 }}><FieldLabel text="Purchase UOM" />{uomSelect('Purchase UOM', 'purchaseUomId')}</Grid>
              <Grid size={{ xs: 12, md: 3 }}><FieldLabel text="Sales UOM" />{uomSelect('Sales UOM', 'salesUomId')}</Grid>
              <Grid size={{ xs: 12, md: 3 }}><FieldLabel text="Stock UOM" />{uomSelect('Stock UOM', 'stockUomId')}</Grid>
              {stockable ? (
                <>
                  <Grid size={{ xs: 12 }}><Divider sx={{ my: 1 }}><Typography sx={{ fontSize: 12, color: P.muted }}>Inventory control</Typography></Divider></Grid>
                  <Grid size={{ xs: 12, md: 4 }}>{toggle('isStockItem', 'Stockable (tracked in inventory)')}</Grid>
                  <Grid size={{ xs: 12, md: 4 }}>{toggle('isBatchTracked', 'Batch controlled')}</Grid>
                  <Grid size={{ xs: 12, md: 4 }}>{toggle('isSerialTracked', 'Serial controlled')}</Grid>
                  <Grid size={{ xs: 12, md: 3 }}><FieldLabel text="Reorder Level" />{numField('reorderLevel')}</Grid>
                  <Grid size={{ xs: 12, md: 3 }}><FieldLabel text="Min Stock" />{numField('minStock')}</Grid>
                  <Grid size={{ xs: 12, md: 3 }}><FieldLabel text="Max Stock" />{numField('maxStock')}</Grid>
                  <Grid size={{ xs: 12, md: 3 }}><FieldLabel text="Safety Stock" />{numField('safetyStock')}</Grid>
                  <Grid size={{ xs: 12, md: 3 }}><FieldLabel text="Lead Time (days)" />{numField('leadTimeDays')}</Grid>
                  <Grid size={{ xs: 12, md: 3 }}><FieldLabel text="Shelf Life (days)" />{numField('shelfLifeDays')}</Grid>
                  <Grid size={{ xs: 12, md: 3 }}><FieldLabel text="EOQ" />{numField('eoq')}</Grid>
                </>
              ) : (
                <Grid size={{ xs: 12 }}><Alert severity="info" sx={{ borderRadius: '10px' }}>{typeMeta?.label} products are not stocked — inventory control does not apply.</Alert></Grid>
              )}
            </Grid>
          )}

          {/* STEP: Procurement */}
          {step.key === 'procurement' && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>{toggle('canPurchase', 'Can be purchased')}</Grid>
              <Grid size={{ xs: 12, md: 4 }}><FieldLabel text="Purchase / Cost Price" /><TextField fullWidth size="small" type="number" value={f.purchasePrice} onChange={(e) => set('purchasePrice', e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} sx={fieldSx} /></Grid>
              <Grid size={{ xs: 12, md: 4 }}><FieldLabel text="Lead Time (days)" />{numField('leadTimeDays')}</Grid>
              <Grid size={{ xs: 12 }}><Alert severity="info" sx={{ borderRadius: '10px' }}>Add multiple approved vendors (with part numbers and prices) from the product's Vendors tab after saving.</Alert></Grid>
            </Grid>
          )}

          {/* STEP: Sales */}
          {step.key === 'sales' && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>{toggle('canSell', 'Can be sold')}</Grid>
              <Grid size={{ xs: 12, md: 4 }}><FieldLabel text="Selling Price" /><TextField fullWidth size="small" type="number" value={f.basePrice} onChange={(e) => set('basePrice', e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} sx={fieldSx} /></Grid>
              <Grid size={{ xs: 12 }}><Alert severity="info" sx={{ borderRadius: '10px' }}>Customer-specific and tiered pricing is maintained in Price Lists (Sales → Price Lists).</Alert></Grid>
            </Grid>
          )}

          {/* STEP: Tax */}
          {step.key === 'tax' && (
            <Grid container spacing={2}>
              {isService ? (
                <Grid size={{ xs: 12, md: 4 }}><FieldLabel text="SAC Code" /><TextField fullWidth size="small" value={f.sacCode} onChange={(e) => set('sacCode', e.target.value)} sx={fieldSx} /></Grid>
              ) : (
                <Grid size={{ xs: 12, md: 6 }}>
                  <FieldLabel text="HSN Code" />
                  {/* freeSolo so a typed/loaded HSN that isn't in the fetched
                      option page is preserved (a plain Autocomplete fires
                      onChange(null) on blur and would wipe it). A synthetic
                      option keeps the current value selected on edit. */}
                  <Autocomplete size="small" freeSolo options={hsnOptions as any}
                    value={hsnValue as any}
                    inputValue={hsnInput} onInputChange={(_, v) => setHsnInput(v)}
                    getOptionLabel={(o) => (typeof o === 'string' ? o : (o?.hsn_code ? `${o.hsn_code}${o.description ? ` — ${o.description}` : ''}` : ''))}
                    isOptionEqualToValue={(o: any, v: any) => o?.hsn_code === v?.hsn_code}
                    onChange={(_, v: any) => {
                      if (typeof v === 'string') { set('hsnCode', v.trim()); }
                      else { set('hsnCode', v?.hsn_code || ''); if (v?.gst_rate != null) set('gstRate', Number(v.gst_rate) || f.gstRate); }
                    }}
                    onBlur={() => { if (hsnInput && !f.hsnCode) set('hsnCode', hsnInput.trim().split(' ')[0]); }}
                    filterOptions={(o) => o}
                    renderInput={(p) => <TextField {...p} sx={fieldSx} placeholder="Search or type HSN…" />} />
                </Grid>
              )}
              <Grid size={{ xs: 12, md: 3 }}>
                <FieldLabel text="GST Rate" />
                <TextField select fullWidth size="small" value={f.gstRate} onChange={(e) => set('gstRate', Number(e.target.value))} sx={fieldSx}>
                  {GST_RATES.map((r) => <MenuItem key={r} value={r}>{r}%</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>{toggle('isTaxInclusive', 'Price is tax-inclusive')}</Grid>
              <Grid size={{ xs: 12, md: 3 }}>{toggle('reverseCharge', 'Reverse charge applies')}</Grid>
            </Grid>
          )}

          {/* STEP: Accounting */}
          {step.key === 'accounting' && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}><Alert severity="info" sx={{ borderRadius: '10px' }}>All optional — leave blank to use the default account for this item type. Accounts come from the Chart of Accounts.</Alert></Grid>
              {[
                ['inventoryAccountId', 'Inventory Asset Account'],
                ['purchaseAccountId', 'Purchase Account'],
                ['salesAccountId', 'Sales Account'],
                ['cogsAccountId', 'COGS Account'],
                ['expenseAccountId', 'Expense Account'],
                ['consumptionAccountId', 'Consumption Account'],
              ].map(([k, label]) => (
                <Grid size={{ xs: 12, md: 6 }} key={k}>
                  <FieldLabel text={label} />
                  <AccountSelect accounts={accounts as any} value={f[k] ? String(f[k]) : ''} onChange={(v) => set(k, v)} />
                </Grid>
              ))}
            </Grid>
          )}

          {/* STEP: Quality / Production */}
          {step.key === 'quality' && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>{toggle('qcRequired', 'QC required')}</Grid>
              <Grid size={{ xs: 12, md: 4 }}>{toggle('incomingInspection', 'Incoming inspection')}</Grid>
              <Grid size={{ xs: 12, md: 4 }}>{toggle('processQc', 'Process QC')}</Grid>
              <Grid size={{ xs: 12, md: 4 }}>{toggle('finalQc', 'Final QC')}</Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FieldLabel text="Make / Buy" />
                <TextField select fullWidth size="small" value={f.makeOrBuy} onChange={(e) => set('makeOrBuy', e.target.value)} sx={fieldSx}>
                  {['MAKE', 'BUY', 'MAKE_BUY'].map((m) => <MenuItem key={m} value={m}>{m.replace('_', ' & ')}</MenuItem>)}
                </TextField>
              </Grid>
              {isCapital && (
                <Grid size={{ xs: 12, md: 4 }}><FieldLabel text="Asset Category Id" />{numField('assetCategoryId')}</Grid>
              )}
              {f.canManufacture && (
                <Grid size={{ xs: 12 }}><Alert severity="info" sx={{ borderRadius: '10px' }}>Define the BOM and routing for this manufactured item in Manufacturing → BOM after saving.</Alert></Grid>
              )}
            </Grid>
          )}

          {/* STEP: Review */}
          {step.key === 'review' && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}><Typography sx={{ fontWeight: 800, color: P.strong, mb: 1 }}>Review</Typography></Grid>
              {[
                ['Type', typeMeta?.label], ['Name', f.productName], ['Category', f.category || '—'],
                ['Primary UOM', uoms.find((u) => u.id === Number(f.uomId))?.uom_code || '—'],
                ['Stockable', stockable ? (f.isStockItem ? 'Yes' : 'No') : 'No'],
                ['Can purchase', f.canPurchase ? 'Yes' : 'No'], ['Can sell', f.canSell ? 'Yes' : 'No'],
                ['Can manufacture', f.canManufacture ? 'Yes' : 'No'], ['Can capitalize', f.canCapitalize ? 'Yes' : 'No'],
                [isService ? 'SAC' : 'HSN', (isService ? f.sacCode : f.hsnCode) || '—'], ['GST', `${f.gstRate}%`],
                ['Purchase price', `₹${f.purchasePrice}`], ['Selling price', `₹${f.basePrice}`],
              ].map(([label, val]) => (
                <Grid size={{ xs: 6, md: 3 }} key={label as string}>
                  <Typography sx={{ fontSize: 11, color: P.label, textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: P.strong }}>{String(val ?? '—')}</Typography>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Step nav */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, pt: 2, borderTop: `1px solid ${P.line}` }}>
            <Button disabled={activeStep === 0} onClick={() => setActiveStep((s) => Math.max(0, s - 1))} startIcon={<ArrowBackIcon />} sx={outBtn}>Back</Button>
            {activeStep < steps.length - 1
              ? <Button variant="contained" onClick={() => setActiveStep((s) => Math.min(steps.length - 1, s + 1))} endIcon={<ArrowForward />} sx={priBtn}>Next</Button>
              : <Button variant="contained" startIcon={<Save />} onClick={save} disabled={saving} sx={priBtn}>{saving ? 'Saving…' : isEdit ? 'Update Product' : 'Create Product'}</Button>}
          </Box>
        </Paper>
      </Box>

      <Snackbar open={!!toast} autoHideDuration={2500} onClose={() => setToast(null)} message={toast || ''} />
    </Box>
  );
};

export default ProductWorkspacePage;
